import { useMemo } from "react";
import { sampleUploaderByAttachmentId } from "@/engine/sample";
import type { Project } from "@/engine/types";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { collectFileEntries, type FileEntry } from "./file-index";
import { fileMockEntries, useFileMockCase } from "./file-mock";

/** 카탈로그의 플랜. 편집 중인 플랜은 저장 전 첨부가 보이도록 편집기 내용을 쓴다. */
export function useIndexedProjects(): Project[] {
  const catalog = useCatalogStore((s) => s.projects);
  const activeId = useProjectStore((s) => s.activeId);
  const editor = useProjectStore((s) => s.project);
  return useMemo(() => {
    const projects = Object.values(catalog).map((project) => (activeId && project.id === activeId ? editor : project));
    return projects.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : a.name.localeCompare(b.name, "ko")));
  }, [catalog, activeId, editor]);
}

/** 목업 사례가 켜져 있으면 그 목록을, 아니면 저장된 첨부를 쓴다. `projectId`가 있으면 많이·적게는 그 플랜만 채운다. */
export function useDisplayedFiles(projectId?: string): FileEntry[] {
  const scenario = useFileMockCase();
  const projects = useIndexedProjects();
  const live = useFileEntries(projectId);
  return useMemo(() => {
    if (scenario === "live") return live;
    return fileMockEntries(scenario, {
      plans: projects.map((project) => ({ id: project.id, name: project.name })),
      focusPlanId: projectId,
    });
  }, [scenario, live, projects, projectId]);
}

/** `projectId`가 있으면 그 플랜만, 없으면 워크스페이스 전체. */
export function useFileEntries(projectId?: string): FileEntry[] {
  const projects = useIndexedProjects();
  return useMemo(() => {
    const known = sampleUploaderByAttachmentId();
    const entries = collectFileEntries(projectId ? projects.filter((project) => project.id === projectId) : projects);
    return entries.map((entry) => (entry.addedBy ? entry : { ...entry, addedBy: known.get(entry.attachment.id) ?? "" }));
  }, [projects, projectId]);
}
